export default function DrawingInsightSection() {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <section id="drawing-insight" className="pt-8 pb-6 lg:pb-12 cinematic-bg-light-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-4">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-normal mb-4 bg-[#385B47] text-white">
              <span aria-label="sparkle">✦</span>
              Free · No commitment
            </div>

            <h2 
              className="text-4xl lg:text-5xl font-normal cinematic-text-dark mb-4"
            >
              Not sure if therapy is for you? Start here — for free
            </h2>
            <p className="text-lg text-[#312921]/85 max-w-4xl mx-auto" style={{ letterSpacing: '0.01em' }}>
              If you're not ready to dive into the work yet, this is a gentle way to experience how I work — I'll offer you a personal reflection based on your drawing.
            </p>
          </div>

          {/* Content Card */}
          <div className="bg-white px-5 lg:px-6 pb-5 lg:pb-6 rounded-3xl border border-gray-100 shadow-lg">

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-y-0 lg:gap-x-8 items-stretch">
            {/* Left Column - How it works */}
            <div className="rounded-xl pt-8 px-3 md:px-8 pb-2 flex flex-col">
              <h3 
                className="text-2xl font-normal cinematic-text-dark mb-6"
              >
                How it works
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D6794E] text-white rounded-full flex items-center justify-center font-normal flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="text-base text-[#312921]/85 leading-relaxed">
                      Create any drawing — on a topic that feels important to you, or simply anything that comes to mind
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D6794E] text-white rounded-full flex items-center justify-center font-normal flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="text-base text-[#312921]/85 leading-relaxed mb-3">
                      Send it to me via:
                    </p>
                    <div className="flex gap-3">
                      <a 
                        href="https://t.me/YuvatiArt" 
                        className="w-10 h-10 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer"
                        title="Telegram"
                      >
                        <i className="ri-telegram-line text-xl text-white"></i>
                      </a>
                      <a 
                        href="https://wa.me/message/WQKZAK5P3C77F1" 
                        className="w-10 h-10 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer"
                        title="WhatsApp"
                      >
                        <i className="ri-whatsapp-line text-xl text-white"></i>
                      </a>
                      <a 
                        href="mailto:yuliya@yuvati.art" 
                        className="w-10 h-10 bg-[#D6794E] rounded-full flex items-center justify-center hover:bg-[#385B47] transition-colors cursor-pointer"
                        title="Email"
                      >
                        <i className="ri-mail-line text-xl text-white"></i>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D6794E] text-white rounded-full flex items-center justify-center font-normal flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="text-base text-[#312921]/85 leading-relaxed">
                      Receive personal feedback
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={scrollToContact}
                className="w-full mt-6 px-8 py-3 bg-[#D6794E] hover:bg-[#385B47] text-white font-normal rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Send your drawing
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {/* Right Column - What you'll get */}
            <div className="rounded-xl pt-8 px-3 md:px-8 pb-2 flex flex-col">
              <h3 
                className="text-2xl font-normal cinematic-text-dark mb-6"
              >
                What you'll get
              </h3>
              
              <div className="space-y-5 flex-1">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#D6794E] text-xl"></i>
                  </div>
                  <p className="text-base text-[#312921]/85 leading-relaxed">
                    A short personal reflection from me on your emotional state
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#D6794E] text-xl"></i>
                  </div>
                  <p className="text-base text-[#312921]/85 leading-relaxed">
                    Insight into how you relate to and express emotions
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#D6794E] text-xl"></i>
                  </div>
                  <p className="text-base text-[#312921]/85 leading-relaxed">
                    Reflection on your sense of self and personal boundaries
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#D6794E] text-xl"></i>
                  </div>
                  <p className="text-base text-[#312921]/85 leading-relaxed">
                    Awareness of inner themes and personal resources
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 rounded-3xl border border-[#D6794E] shadow-sm" style={{ backgroundColor: '#DBCEC5' }}>
                <p className="text-sm text-[#385B47] leading-relaxed">
                  <i className="ri-information-line text-[#385B47] mr-2"></i>
                  This is a free offering to help you get a sense of my approach. For deeper exploration, consider booking a full session.
                </p>
                <p className="text-sm text-[#385B47] leading-relaxed mt-2">
                  By sending your drawing, you consent to joining my mailing list. You can opt out at any time.
                </p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>
    </>
  );
}